import { useState } from "react";
import { X, Loader2, AlertCircle, BadgeCheck, Hash, CheckCircle2 } from "lucide-react";
import {
  useAdminMemberNumberPreviewQuery,
  useAdminIssueMemberNumbersMutation,
} from "../api/upgradeApi";

/**
 * Issuing Member numbers to the fully endorsed applicants in a category.
 *
 * Numbers come from one running Institute series and are written to the members
 * register, where nothing later undoes them — so this is deliberately a two-step
 * action. The preview is the whole point of the dialog: it names every person
 * about to be numbered and the exact number each will get, before anything is
 * written. Nobody should discover what the button did by reading the register
 * afterwards.
 *
 * The server, not this component, decides who is eligible and what the next
 * number is. Two copies of that reasoning would eventually disagree, and the
 * one that loses would have already stamped numbers on people.
 */
export default function IssueMemberNumbersModal({
  categoryId, categoryName, dietId, dietTitle, onClose,
}: {
  categoryId: string | number;
  categoryName: string;
  /**
   * Narrow the batch to one review cycle's applicants. The grade still travels
   * with it: a cycle holds every grade, and the Member series must never be
   * handed to a Fellow who happens to be sitting in the same cycle.
   */
  dietId?: string;
  dietTitle?: string;
  onClose: () => void;
}) {
  const { data, isLoading, isError, error, refetch } =
    useAdminMemberNumberPreviewQuery({ to_category_id: categoryId, review_diet_id: dietId });

  const [issue, { isLoading: issuing }] = useAdminIssueMemberNumbersMutation();

  const [result, setResult] = useState<any>(null);
  const [failure, setFailure] = useState<string | null>(null);
  // Typed confirmation. The dialog is destructive in the way a delete is not —
  // it cannot be reversed by clicking the opposite button — so the last step is
  // deliberately not a single click.
  const [confirmText, setConfirmText] = useState("");

  const preview  = data?.data ?? data;
  const series   = preview?.series ?? {};
  const eligible: any[] = preview?.eligible ?? [];
  const skipped  = preview?.skipped ?? {};
  const numbered: any[] = skipped?.already_numbered ?? [];
  const orphans: any[]  = skipped?.no_member_record ?? [];

  const first = eligible[0]?.proposed_member_no;
  const last  = eligible[eligible.length - 1]?.proposed_member_no;

  const doIssue = async () => {
    setFailure(null);
    try {
      const res = await issue({ to_category_id: categoryId, review_diet_id: dietId }).unwrap();
      setResult(res);
    } catch (e: any) {
      setFailure(
        e?.data?.message ?? e?.error ?? "Could not issue the numbers. Nothing was written.",
      );
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Hash className="w-4 h-4 text-indigo-600" />
              Issue {categoryName} numbers
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Fully endorsed applicants who do not already hold a number.
              {/* Named, not implied: the same dialog numbers a whole grade from
                  the category screen and one sitting's applicants from a review
                  cycle, and the two are very different batches. */}
              {dietId ? ` In “${dietTitle ?? "this review cycle"}” only.` : ""}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {isLoading && (
            <div className="flex justify-center py-10">
              <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
            </div>
          )}

          {isError && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose-50 border border-rose-100 text-rose-700 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                {(error as any)?.data?.message ?? "Could not load the numbering preview."}
                <button onClick={() => refetch()} className="ml-2 underline font-semibold">Retry</button>
              </div>
            </div>
          )}

          {/* ── After issuing ─────────────────────────────────────────────── */}
          {result && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-800 text-sm font-semibold">
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                {result.message}
              </div>

              {(result.data?.issued?.length ?? 0) > 0 && (
                <IssuedTable rows={result.data.issued} />
              )}

              <p className="text-xs text-gray-500">
                The numbers are now on the members register and appear in the “New Members Number”
                column of the Excel and PDF exports.
              </p>
            </div>
          )}

          {/* ── Before issuing ────────────────────────────────────────────── */}
          {!result && preview && (
            <div className="space-y-5">
              {/* Where the series stands. Shown before the list, because it is
                  what makes the proposed numbers checkable — the secretariat
                  knows what the last number issued was. */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Figure
                  label="Last number issued"
                  value={series.highest_text ?? "None yet"}
                  sub={
                    // Which of the two sources decided it. Issuing takes the
                    // higher of the register and the counter saved in settings,
                    // so saying which one is speaking makes a surprising "next"
                    // traceable without opening the settings screen.
                    series.seeded_from === "register"
                      ? "Read from the register"
                      : series.seeded_from === "settings"
                        ? "From the counter in settings"
                        : "Nothing issued yet"
                  }
                />
                <Figure label="Next number" value={series.next_text ?? "—"} tone="good" />
                <Figure
                  label="To be issued"
                  value={String(eligible.length)}
                  sub={eligible.length ? `${first} — ${last}` : "Nobody is waiting"}
                  tone={eligible.length ? "good" : "plain"}
                />
              </div>

              {series.seeded_from === "configured_start" && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                  Nothing has been issued yet, so the series starts at <b>{series.next_text}</b>.
                  Check that against the last number issued by hand before continuing — once these
                  are written they are the register. The start is on the{" "}
                  <a href="/admin/settings" className="underline font-semibold">
                    portal settings
                  </a>{" "}
                  screen.
                </p>
              )}

              {eligible.length > 0 ? (
                <div>
                  <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Will be numbered ({eligible.length})
                  </div>
                  <div className="border rounded-xl overflow-hidden max-h-72 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50">
                        <tr className="text-left text-gray-500 border-b">
                          <th className="px-3 py-2 font-medium">Applicant</th>
                          <th className="px-3 py-2 font-medium">Current no.</th>
                          <th className="px-3 py-2 font-medium">New number</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {eligible.map((row) => (
                          <tr key={row.application_id}>
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-900">{row.name || "—"}</div>
                              <div className="text-xs text-gray-400">{row.email}</div>
                            </td>
                            <td className="px-3 py-2 text-gray-600">{row.membership_no || "—"}</td>
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 font-bold">
                                <BadgeCheck className="w-3 h-3" />
                                {row.proposed_member_no}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-xl border bg-gray-50 text-sm text-gray-700">
                  No fully endorsed applicant is waiting for a number. Everyone who qualifies
                  already holds one.
                </div>
              )}

              {/* Passed over, and why. A count that quietly excluded these would
                  hide a data fault the secretariat has to fix. */}
              {numbered.length > 0 && (
                <SkippedList
                  title={`Already numbered — skipped (${numbered.length})`}
                  rows={numbered}
                  render={(r) => `${r.name} · ${r.member_member_no}`}
                />
              )}
              {orphans.length > 0 && (
                <SkippedList
                  title={`No members record — cannot be numbered (${orphans.length})`}
                  rows={orphans}
                  render={(r) => `${r.name} · ${r.email ?? "no email"}`}
                  tone="warn"
                />
              )}

              {failure && (
                <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-rose-50 border border-rose-100 text-rose-700 text-sm">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  {failure}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t flex items-center justify-between gap-3 flex-wrap">
          {!result && eligible.length > 0 ? (
            <>
              <label className="text-xs text-gray-600 flex items-center gap-2">
                Type <b className="font-mono">ISSUE</b> to confirm
                <input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  className="w-24 px-2 py-1 border rounded-md text-sm font-mono outline-none focus:ring-2 focus:ring-indigo-100"
                  placeholder="ISSUE"
                />
              </label>
              <div className="flex items-center gap-2">
                <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  onClick={doIssue}
                  disabled={issuing || confirmText !== "ISSUE"}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
                >
                  {issuing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hash className="w-4 h-4" />}
                  Issue {eligible.length} number{eligible.length === 1 ? "" : "s"}
                </button>
              </div>
            </>
          ) : (
            <button onClick={onClose} className="ml-auto px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Figure({
  label, value, sub, tone = "plain",
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "plain" | "good";
}) {
  const tones = {
    plain: "bg-white border-gray-200 text-gray-900",
    good:  "bg-emerald-50 border-emerald-100 text-emerald-800",
  } as const;

  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-60">{label}</div>
      <div className="text-lg font-black mt-0.5 font-mono">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

function SkippedList({
  title, rows, render, tone = "plain",
}: {
  title: string;
  rows: any[];
  render: (row: any) => string;
  tone?: "plain" | "warn";
}) {
  return (
    <details className={`rounded-xl border px-3 py-2 ${tone === "warn" ? "border-amber-200 bg-amber-50" : "border-gray-200 bg-gray-50"}`}>
      <summary className="text-xs font-bold text-gray-700 cursor-pointer">{title}</summary>
      <ul className="mt-2 space-y-1 text-xs text-gray-600 max-h-40 overflow-y-auto">
        {rows.map((r) => (
          <li key={r.application_id}>{render(r)}</li>
        ))}
      </ul>
    </details>
  );
}

function IssuedTable({ rows }: { rows: any[] }) {
  return (
    <div className="border rounded-xl overflow-hidden max-h-72 overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-gray-50">
          <tr className="text-left text-gray-500 border-b">
            <th className="px-3 py-2 font-medium">Member</th>
            <th className="px-3 py-2 font-medium">Membership no.</th>
            <th className="px-3 py-2 font-medium">Issued</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => (
            <tr key={r.application_id}>
              <td className="px-3 py-2 font-medium text-gray-900">{r.name}</td>
              <td className="px-3 py-2 text-gray-600">{r.membership_no || "—"}</td>
              <td className="px-3 py-2 font-mono font-bold text-emerald-700">{r.member_member_no}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
